import { Divider, makeStyles, Tooltip } from '@material-ui/core';
import { PieChartOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import React from 'react';
import { BarChart2, Clipboard, ExternalLink, FolderPlus, Target } from 'react-feather';
import { Link, useLocation } from 'react-router-dom';
import { usePrefixTranslation } from '../../../i18n';
import { useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { IconButton } from '../Button';
import { Avatar } from '../Chip';

const useSidebarClasses = makeStyles({
    root: {
        position: 'sticky',
        top: 65,
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 65px)',
        padding: '20px 10px',
        overflow: 'hidden',
        transition: `all ${theme.transitions.default}s, height 0s`,
        '&:not(.open)': {
            flex: '0 0 60px',
            background: theme.colors.surface[1],
        },
        '&.open': {
            flex: '0 0 244px',
            background: theme.colors.white,
        },
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        whiteSpace: 'nowrap',
        gap: 10,
    },
    toggle: {
        transition: `opacity ${theme.transitions.default}s`,
        '&:not(.open)': {
            opacity: 0,
        },
        '&.open': {
            opacity: 1,
        },
    },
    divider: {
        transition: `background ${theme.transitions.default}s`,
        '&:not(.open)': {
            background: theme.colors.green[4],
        },
        '&.open': {
            background: theme.colors.neutral[3],
        },
    },
});

/**
 * Lateral Navigation menu component.
 */
export const Sidebar = () => {
    const classes = useSidebarClasses();
    const location = useLocation();
    const { t } = usePrefixTranslation('global', { global: 'sidebar.Sidebar' });
    const sidebar = useTypedSelector(state => state.global_sidebar);
    const cls = { open: sidebar.open };

    return (
        <div className={clsx(classes.root, cls)}>
            <div className={classes.column}>
                <Company name='Georgia Pacific' location='Brewton, AL' />
                <Divider className={clsx(classes.divider, cls)} />
                <nav>
                    {[
                        { label: t('dashboard'), href: '/', icon: <BarChart2 strokeWidth={1.5} /> },
                        { label: t('assets'), href: '/assets', icon: <Target strokeWidth={1.5} /> },
                        { label: t('projects'), href: '/projects', icon: <Clipboard strokeWidth={1.5} /> },
                        { label: t('new-plan'), href: '/plans/create', icon: <FolderPlus strokeWidth={1.5} /> },
                        { label: t('reports'), href: '/reports', icon: <PieChartOutlined strokeWidth={1.5} /> },
                    ].map(({ label, href, icon }) => (
                        <MenuItem
                            key={label}
                            open={sidebar.open}
                            href={href}
                            icon={icon}
                            label={label}
                            selected={location.pathname === href}
                        />
                    ))}
                </nav>
            </div>
            <div style={{ flex: 1 }} />
            <div className={clsx(classes.column, classes.toggle, cls)}>
                <PortalLink />
                <Divider className={clsx(classes.divider, cls)} />
                <Copyright company={'Gecko Robotics'} year={2021} product={'Gecko Nest'} version={'v1.0'} />
            </div>
        </div>
    );
};

const useCompanyClasses = makeStyles({
    root: {
        display: 'flex',
    },
    labels: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        marginLeft: 10,
        color: theme.colors.neutral[1],
        '& > span:first-of-type': {
            ...theme.typography.headings.h6,
            textTransform: 'uppercase',
        },
        '& > span:last-of-type': {
            ...theme.typography.content.caption.default,
        },
    },
});

/**
 * Company header for the sidebar.
 *
 * @param props.name company name
 * @param props.location company location
 * @param props.src company image
 */
export const Company = (props: { name: string; location: string; src?: string }) => {
    const classes = useCompanyClasses();

    return (
        <div className={classes.root}>
            <Avatar {...props} />
            <div className={classes.labels}>
                <span>{props.name}</span>
                <span>{props.location}</span>
            </div>
        </div>
    );
};

const useMenuItemClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        padding: 10,
        borderRadius: 8,
        cursor: 'pointer',
        ...theme.typography.content.label.medium,
        textDecoration: 'none',
        '&.open': {
            color: theme.colors.neutral[2],
            '&.selected': { background: theme.colors.neutral[6], color: theme.colors.darkBlue[2] },
            '&:hover': { color: theme.colors.green[4] },
        },
        '&:not(.open)': {
            color: theme.colors.white,
            '&.selected': { background: theme.colors.darkBlue[2] },
            '&:hover': { color: theme.colors.green[4] },
        },
        '& > *': {
            transition: `all ${theme.transitions.default}s`,
            opacity: 1,
        },
        '& > svg': {
            flex: '0 0 24px',
            margin: '0 15px 0 -2px',
        },
        '&:not(.open) > :not(svg)': {
            opacity: 0,
        },
    },
    tooltip: {
        display: 'flex',
        justifyContent: 'center',
        minWidth: 130,
        padding: 8,
        borderRadius: 4,
        boxShadow: '0 1px 1px 0 rgba(0, 0, 0, 0.14)',
        background: theme.colors.neutral[4],
        color: theme.colors.darkBlue[2],
        ...theme.typography.content.label.medium,
        '& .MuiTooltip-arrow': {
            color: theme.colors.neutral[4],
        },
    },
});

/**
 * A single sidebar menu item.
 *
 * @param props.open the current menu state
 * @param props.icon the icon to be displayed in the menu item
 * @param props.label the item label
 * @param props.selected if the current menu item is currently selected
 * @param props.data optional component to be displayed beside the label
 */
const MenuItem = (props: {
    open: boolean;
    href: string;
    icon: React.ReactNode;
    label: string;
    data?: React.ReactNode;
    selected?: boolean;
}) => {
    const classes = useMenuItemClasses();
    const cls = { open: props.open, selected: !!props.selected };

    return (
        <Tooltip classes={{ tooltip: classes.tooltip }} title={!props.open ? props.label : ''} placement='right' arrow>
            <Link className={clsx(classes.root, cls)} to={props.href}>
                {props.icon}
                <span>{props.label}</span>
                <div style={{ flex: 1 }} />
                {props.data}
            </Link>
        </Tooltip>
    );
};

const usePortalLinkClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        padding: 12,
        borderRadius: 5,
        background: theme.colors.neutral[6],
    },
    labels: {
        display: 'flex',
        flexDirection: 'column',
        '& > span:first-of-type': {
            color: theme.colors.neutral[1],
            ...theme.typography.content.label.default,
            marginBottom: 3,
        },
        '& > span:last-of-type': {
            cursor: 'pointer',
            color: theme.colors.green[3],
            ...theme.typography.content.caption.default,
        },
    },
    icon: {
        padding: 8,
        margin: -4,
        color: theme.colors.neutral[1],
    },
});

/**
 * Small component with information about the gecko portal.
 */
const PortalLink = () => {
    const classes = usePortalLinkClasses();
    const { t } = usePrefixTranslation('global', { global: 'sidebar.Sidebar' });

    return (
        <div className={classes.root}>
            <div className={classes.labels}>
                <span>{'Gecko Portal'}</span>
                <span>{t('login')}</span>
            </div>
            <div style={{ flex: 1 }} />
            <a href='https://www.geckorobotics.com/' target='_blank' rel='noopener noreferrer'>
                <IconButton className={classes.icon} size='medium'>
                    <ExternalLink />
                </IconButton>
            </a>
        </div>
    );
};

const useCopyrightClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        color: theme.colors.neutral[2],
        ...theme.typography.content.caption.default,
        '& > span:first-of-type': {
            marginBottom: 8,
        },
    },
});

/**
 * A component to display copyright information.
 *
 * @param props information of the company and product
 */
const Copyright = (props: { company: string; year: number; product: string; version: string }) => {
    const classes = useCopyrightClasses();

    return (
        <div className={classes.root}>
            <span>{`© ${props.year} ${props.company} Inc.`}</span>
            <span>{`${props.product} ${props.version}`}</span>
        </div>
    );
};
